import moment from "moment-timezone";

export const TEPCO_CHIBA2_SEASON_ID = "cmryyb2lk000181zc6fk720qn";
export const TEPCO_CHIBA2_CHUNK_CONCURRENCY = 3;

export const toTepcoUtcBounds = (dateRange) => ({
  startUtc: moment.tz(`${dateRange[0]} 00:00:00`, "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss"),
  endUtc: moment.tz(`${dateRange[1]} 00:00:00`, "Asia/Tokyo").add(1, "day").utc().format("YYYY-MM-DD HH:mm:ss"),
});

export const splitTepcoDateRange = (dateRange, maxDays = 14) => {
  if (!Array.isArray(dateRange) || dateRange.length !== 2 || maxDays < 1) return [];
  const lastDate = moment.tz(`${dateRange[1]} 00:00:00`, "Asia/Tokyo");
  let chunkStart = moment.tz(`${dateRange[0]} 00:00:00`, "Asia/Tokyo");
  const chunks = [];

  while (chunkStart.isSameOrBefore(lastDate, "day")) {
    const chunkEnd = chunkStart.clone().add(maxDays - 1, "days");
    const actualChunkEnd = chunkEnd.isAfter(lastDate, "day") ? lastDate.clone() : chunkEnd;
    chunks.push([chunkStart.format("YYYY-MM-DD"), actualChunkEnd.format("YYYY-MM-DD")]);
    chunkStart = actualChunkEnd.clone().add(1, "day");
  }
  return chunks;
};

export const groupTepcoDateRangeChunks = (chunks, concurrency = TEPCO_CHIBA2_CHUNK_CONCURRENCY) => {
  if (!Array.isArray(chunks) || concurrency < 1) return [];
  const groups = [];
  for (let index = 0; index < chunks.length; index += concurrency) {
    groups.push(chunks.slice(index, index + concurrency));
  }
  return groups;
};

export const buildTepcoPhotoShootTrendQuery = ({ seasonIds, dateRange, grain = "day" }) => {
  const { startUtc, endUtc } = toTepcoUtcBounds(dateRange);
  if (seasonIds.length === 1 && seasonIds[0] === TEPCO_CHIBA2_SEASON_ID) {
    const bucket = grain === "hour"
      ? "DATE_FORMAT(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR), '%Y-%m-%d %H:00')"
      : "DATE(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR))";
    return {
      operator: "tepco-chiba2",
      query: `
        SELECT
          ${bucket} AS bucket,
          COUNT(*) AS shooting_count,
          COUNT(DISTINCT pae.player_id) AS unique_photographer_count
        FROM power_asset_event pae
        WHERE pae.game_space_id = '${TEPCO_CHIBA2_SEASON_ID}'
          AND pae.type = 'PHOTOGRAPH'
          AND pae.occurred_datetime >= '${startUtc}'
          AND pae.occurred_datetime < '${endUtc}'
        GROUP BY bucket
        ORDER BY bucket ASC;`,
      database: "KpiDashboard",
      type: "list",
    };
  }
  const ids = seasonIds.map((id) => `'${id}'`).join(", ");
  const bucket = grain === "hour"
    ? "DATE_FORMAT(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR), '%Y-%m-%d %H:00')"
    : "DATE(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR))";
  const assetType = `CASE
    WHEN UPPER(COALESCE(pa.type, '')) IN ('KANKIKO', 'VENTILATION', 'VENT') THEN 'kankiko'
    WHEN UPPER(COALESCE(pa.type, '')) IN ('GROUND_EQUIPMENT', 'GROUND_EQUIP', 'GE') THEN 'groundEquip'
    WHEN UPPER(COALESCE(pa.type, '')) IN ('MANHOLE', 'MH') THEN 'mh'
    WHEN UPPER(COALESCE(pa.type, '')) IN ('HANDHOLE', 'HH') THEN 'hh'
    WHEN UPPER(COALESCE(pa.type, '')) IN ('POWER_POLE', 'DENCHU', 'POLE') THEN 'denchu'
    ELSE 'denchu'
  END`;
  return {
    query: `
      SELECT ${bucket} AS bucket, ${assetType} AS asset_type,
        COUNT(*) AS shooting_count, COUNT(DISTINCT pae.player_id) AS unique_photographer_count,
        0 AS first_time_photographer_count
      FROM power_asset_event pae
      LEFT JOIN power_asset pa ON pa.id = pae.power_asset_id
      WHERE pae.game_space_id IN (${ids}) AND pae.type = 'PHOTOGRAPH'
        AND pae.occurred_datetime >= '${startUtc}' AND pae.occurred_datetime < '${endUtc}'
      GROUP BY ${bucket}, ${assetType}
      UNION ALL
      SELECT ${bucket} AS bucket, 'ALL' AS asset_type, 0 AS shooting_count,
        COUNT(DISTINCT pae.player_id) AS unique_photographer_count, 0 AS first_time_photographer_count
      FROM power_asset_event pae
      WHERE pae.game_space_id IN (${ids}) AND pae.type = 'PHOTOGRAPH'
        AND pae.occurred_datetime >= '${startUtc}' AND pae.occurred_datetime < '${endUtc}'
      GROUP BY ${bucket}
      ORDER BY bucket ASC, asset_type ASC;`,
    database: "KpiDashboard",
    type: "list",
  };
};

export const isTepcoChiba2Season = (seasonIds) => (
  Array.isArray(seasonIds)
  && seasonIds.length === 1
  && seasonIds[0] === TEPCO_CHIBA2_SEASON_ID
);

// This helper is called only from the TEPCO home Saga. Its selected season IDs
// come directly from game_space.id in pictreeSeasonSaga, so no separate or
// inferred game-space mapping is required.
export const buildTepcoChiba2AssetMetricsQuery = ({ startUtc, endUtc }) => ({
  query: `
    SELECT
      COUNT(power_asset_id) AS total_photo_power_assets,
      SUM(
        CASE
          WHEN power_asset_id IS NOT NULL AND event_count > 1 THEN 1
          ELSE 0
        END
      ) AS dupe_asset_count
    FROM (
      SELECT
        pae.power_asset_id,
        COUNT(*) AS event_count
      FROM power_asset_event pae
      WHERE pae.game_space_id = '${TEPCO_CHIBA2_SEASON_ID}'
        AND pae.type = 'PHOTOGRAPH'
        AND pae.occurred_datetime >= '${startUtc}'
        AND pae.occurred_datetime < '${endUtc}'
      GROUP BY pae.power_asset_id
    ) AS asset_event_counts;`,
  database: "KpiDashboard",
  type: "list",
});

export const normalizeTepcoChiba2AssetMetrics = (row) => {
  if (!row || row.total_photo_power_assets == null || row.dupe_asset_count == null) {
    throw new Error("Invalid TEPCO Chiba 2 asset metrics response");
  }
  const countDenchu = Number(row.total_photo_power_assets);
  const countDupeDenchu = Number(row.dupe_asset_count);
  if (!Number.isFinite(countDenchu) || !Number.isFinite(countDupeDenchu)) {
    throw new Error("Invalid TEPCO Chiba 2 asset metrics values");
  }
  return {
    countDenchu,
    countAsset: countDenchu,
    countDupeDenchu,
  };
};

const normalizeRows = (response) => {
  if (Array.isArray(response)) return response;
  if (response && Array.isArray(response.body)) return response.body;
  if (response && typeof response.body === "string") {
    try { return normalizeRows(JSON.parse(response.body)); } catch (e) { return []; }
  }
  if (response && response.bucket !== undefined) return [response];
  if (response && response.body && response.body.bucket !== undefined) return [response.body];
  return [];
};

export const normalizeTepcoChiba2AssetMetricsResponse = (response) => {
  if (response && response.total_photo_power_assets !== undefined) return response;
  if (response && response.body && response.body.total_photo_power_assets !== undefined) {
    return response.body;
  }
  if (response && typeof response.body === "string") {
    try { return normalizeTepcoChiba2AssetMetricsResponse(JSON.parse(response.body)); } catch (e) { return undefined; }
  }
  const rows = normalizeRows(response);
  return rows[0];
};

export const mergeTepcoPhotoShootTrendResponses = (responses) => (
  responses.reduce((rows, response) => rows.concat(normalizeRows(response)), [])
);

export const buildTepcoChiba2PhotoShootTrendViewModel = ({ response, buckets, grain = "day" }) => {
  const rows = normalizeRows(response);
  const bucketLength = grain === "hour" ? 16 : 10;
  const countDenchu = buckets.map((bucket) => {
    const row = rows.find((item) => String(item.bucket).slice(0, bucketLength) === bucket);
    return Number(row?.shooting_count || 0);
  });
  const unique = buckets.map((bucket) => {
    const row = rows.find((item) => String(item.bucket).slice(0, bucketLength) === bucket);
    return Number(row?.unique_photographer_count || 0);
  });
  return {
    date: buckets,
    countDenchu: countDenchu.every((value) => value === 0) ? false : countDenchu,
    countKankiko: false,
    countGroundEquip: false,
    countMh: false,
    countHh: false,
    unique,
    newUser: buckets.map(() => null),
  };
};

export const buildTepcoPhotoShootTrendViewModel = ({ response, dateRange, grain = "day", buckets }) => {
  const rows = normalizeRows(response);
  const result = { date: buckets, countDenchu: [], countKankiko: [], countGroundEquip: [], countMh: [], countHh: [], unique: [], newUser: [] };
  buckets.forEach((bucket) => {
    const bucketRows = rows.filter((row) => String(row.bucket).slice(0, grain === "hour" ? 16 : 10) === bucket);
    const value = (type, key) => bucketRows.filter((row) => row.asset_type === type).reduce((sum, row) => sum + Number(row[key] || 0), 0);
    const all = bucketRows.find((row) => row.asset_type === "ALL");
    result.countDenchu.push(value("denchu", "shooting_count"));
    result.countKankiko.push(value("kankiko", "shooting_count"));
    result.countGroundEquip.push(value("groundEquip", "shooting_count"));
    result.countMh.push(value("mh", "shooting_count"));
    result.countHh.push(value("hh", "shooting_count"));
    result.unique.push(Number(all?.unique_photographer_count || 0));
    // 初回撮影者は全season履歴の定義と性能課題が未解決のためDeferred。
    // 既存Redux/UI shapeのみ維持し、値は表示対象外としてnullにする。
    result.newUser.push(null);
  });
  const allZero = (arr) => arr.every((value) => value === 0);
  ["countDenchu", "countKankiko", "countGroundEquip", "countMh", "countHh"].forEach((key) => {
    if (allZero(result[key])) result[key] = false;
  });
  return result;
};
